import { useEffect, useRef } from 'react';
import { getRouteImporter } from '../routing/routeImports';

export const canPrefetchRouteCode = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return navigator.onLine !== false && document.visibilityState !== 'hidden'
    && !(connection && (connection.saveData
      || ['slow-2g', '2g', '3g'].includes(connection.effectiveType)
      || (typeof connection.downlink === 'number' && connection.downlink < 1.5)
      || (typeof connection.rtt === 'number' && connection.rtt >= 300)));
};

const isVisible = (link) => {
  if (link.closest('[hidden], [aria-hidden="true"]') || !link.getClientRects().length) return false;
  const style = window.getComputedStyle(link);
  return style.visibility !== 'hidden' && style.display !== 'none';
};

const isInViewport = (link) => {
  const rect = link.getBoundingClientRect();
  return rect.bottom > 0 && rect.right > 0
    && rect.top < window.innerHeight && rect.left < window.innerWidth;
};

// Read only links actually rendered in this layout. Speculation never renders a
// page or calls its data loaders, and never changes which links a user can access.
export const attachRoutePrefetch = (root, maxIdlePrefetches = 4) => {
  if (!root) return () => {};
  let active = true;
  let idleTask;
  let refreshTask;
  let loading = false;
  let idleStarted = 0;
  const queue = [];
  const queued = new Set();
  const observed = new Set();
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  const importerForLink = (link) => {
    if (!root.contains(link) || link.hasAttribute('download')
      || link.getAttribute('aria-disabled') === 'true'
      || (link.target && link.target !== '_self')) return null;
    const href = (link.getAttribute('href') || '').trim();
    if (!href || href.startsWith('#')) return null;
    try {
      const url = new URL(href, window.location.href);
      return url.origin === window.location.origin ? getRouteImporter(url.pathname) : null;
    } catch (_) {
      return null;
    }
  };

  const cancelIdle = () => {
    if (!idleTask) return;
    if (idleTask.type === 'idle') window.cancelIdleCallback(idleTask.id);
    else window.clearTimeout(idleTask.id);
    idleTask = undefined;
  };

  const finishImport = () => {
    loading = false;
    scheduleIdle();
  };

  const scheduleIdle = () => {
    if (!active || idleTask || loading || !queue.length || idleStarted >= maxIdlePrefetches
      || !canPrefetchRouteCode()) return;
    const run = (deadline) => {
      idleTask = undefined;
      if (!active || !canPrefetchRouteCode()) return;
      if (!deadline.didTimeout && deadline.timeRemaining() < 8) {
        scheduleIdle();
        return;
      }
      while (queue.length) {
        const { link, importer } = queue.shift();
        queued.delete(importer);
        if (!importer.isRequested() && importerForLink(link) === importer
          && isVisible(link) && isInViewport(link)) {
          idleStarted += 1;
          loading = true;
          importer.load().catch(() => {}).finally(finishImport);
          break;
        }
      }
      if (idleStarted >= maxIdlePrefetches) {
        intersectionObserver?.disconnect();
        mutationObserver?.disconnect();
        observed.clear();
      }
    };
    idleTask = window.requestIdleCallback
      ? { type: 'idle', id: window.requestIdleCallback(run, { timeout: 1500 }) }
      : { type: 'timeout', id: window.setTimeout(() => run({ didTimeout: true }), 750) };
  };

  const enqueue = (link) => {
    if (!active || !canPrefetchRouteCode() || idleStarted >= maxIdlePrefetches) return;
    // Scrolling, collapsing a menu or changing routes must not let stale links
    // occupy the small idle queue and crowd out links that are now visible.
    for (let index = queue.length - 1; index >= 0; index -= 1) {
      const entry = queue[index];
      if (entry.importer.isRequested() || importerForLink(entry.link) !== entry.importer
        || !isVisible(entry.link) || !isInViewport(entry.link)) {
        queued.delete(entry.importer);
        queue.splice(index, 1);
      }
    }
    const importer = importerForLink(link);
    if (!importer || importer.isRequested() || queued.has(importer) || !isVisible(link)
      || queue.length >= maxIdlePrefetches - idleStarted) return;
    queued.add(importer);
    queue.push({ link, importer });
    scheduleIdle();
  };

  const intersectionObserver = window.IntersectionObserver
    ? new window.IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => { if (isIntersecting) enqueue(target); });
    }, { threshold: 0.1 }) : null;

  const discoverLinks = () => {
    if (!active || idleStarted >= maxIdlePrefetches) return;
    if (!canPrefetchRouteCode()) {
      cancelIdle();
      return;
    }
    observed.forEach((link) => {
      if (!root.contains(link)) {
        intersectionObserver?.unobserve(link);
        observed.delete(link);
      }
    });
    root.querySelectorAll('a[href]').forEach((link) => {
      if (!importerForLink(link)) return;
      if (intersectionObserver && !observed.has(link)) {
        observed.add(link);
        intersectionObserver.observe(link);
      }
      if (isVisible(link) && isInViewport(link)) enqueue(link);
    });
  };

  const refresh = () => {
    if (!active || refreshTask) return;
    refreshTask = window.setTimeout(() => {
      refreshTask = undefined;
      discoverLinks();
    }, 0);
  };

  const prefetchIntent = (event) => {
    if (!canPrefetchRouteCode() || !(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || !isVisible(link)) return;
    const importer = importerForLink(link);
    if (importer && !importer.isRequested()) importer.load().catch(() => {});
  };

  const mutationObserver = new window.MutationObserver(refresh);
  mutationObserver.observe(root, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['href', 'class', 'style', 'hidden', 'aria-hidden', 'aria-disabled'],
  });
  root.addEventListener('pointerover', prefetchIntent, { passive: true });
  root.addEventListener('focusin', prefetchIntent);
  root.addEventListener('scroll', refresh, { capture: true, passive: true });
  window.addEventListener('scroll', refresh, { passive: true });
  window.addEventListener('resize', refresh, { passive: true });
  document.addEventListener('visibilitychange', refresh);
  connection?.addEventListener?.('change', refresh);
  window.addEventListener('online', refresh);
  discoverLinks();

  return () => {
    active = false;
    cancelIdle();
    window.clearTimeout(refreshTask);
    intersectionObserver?.disconnect();
    mutationObserver.disconnect();
    root.removeEventListener('pointerover', prefetchIntent);
    root.removeEventListener('focusin', prefetchIntent);
    root.removeEventListener('scroll', refresh, true);
    window.removeEventListener('scroll', refresh);
    window.removeEventListener('resize', refresh);
    document.removeEventListener('visibilitychange', refresh);
    connection?.removeEventListener?.('change', refresh);
    window.removeEventListener('online', refresh);
    queue.length = 0;
    queued.clear();
    observed.clear();
  };
};

const useRoutePrefetch = (pathname) => {
  const rootRef = useRef(null);
  useEffect(() => attachRoutePrefetch(rootRef.current), [pathname]);
  return rootRef;
};

export default useRoutePrefetch;
